import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CanMatch, Route, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CacheService } from '../../shared/services/cache.service';
import { FormatterUtil } from '../../utils/formatter-util';

/**
 * Guard used with the :category route. Only allows the route to match when the
 * first URL segment is a valid category slug. Unknown segments (e.g. random paths
 * like /djhdjhddhbkdjjd) cause the route not to match, so the wildcard ** can
 * redirect to the 404 error page.
 */
@Injectable({ providedIn: 'root' })
export class ValidCategoryGuard implements CanMatch {
  private readonly cacheService = inject(CacheService);
  private readonly platformId = inject(PLATFORM_ID);

  canMatch(_route: Route, segments: UrlSegment[]): Observable<boolean> {
    if (!segments.length) {
      return of(false);
    }
    const segment = segments[0].path;
    return this.cacheService.getCategoriesFromCache().pipe(
      take(1),
      map((categories) => {
        const validSlugs = categories
          .map((c) => (c.name ? FormatterUtil.formatToKebabCase(c.name).toLowerCase() : ''))
          .filter(Boolean);
        return validSlugs.includes(segment.toLowerCase());
      }),
      // On SSR, allow the route if categories API fails so pack gallery still renders with SEO.
      catchError(() => of(!isPlatformBrowser(this.platformId)))
    );
  }
}
